import { Component, EventEmitter, Input, Output, OnInit, OnChanges, OnDestroy, HostListener } from '@angular/core';
import {
  AssistantService,
  AssistantSkill,
  AssistantRoutine,
  AdminCapability,
  UsageReport,
  UsagePeriod,
  SkillInputParam,
  AssistantApiError,
} from 'src/@core/services/assistant/assistant.service';
import { CURRENT_USER } from 'src/@core/services/app.service';
import { environment } from 'src/environments/environment';

type Pane = 'channels' | 'integrations' | 'tools' | 'skills' | 'webhooks' | 'memory' | 'usage' | 'settings';
type Period = 'today' | 'week' | 'month';

interface NavItem { key: Pane; label: string; }

/** Editable form model for the add/edit webhook-skill form. */
interface SkillForm {
  skill_id?: string;
  kind: 'webhook' | 'instruction';     // External API vs uploaded playbook
  name: string;
  description: string;
  method: string;
  url: string;
  trigger_keywords: string;            // comma/space separated in the UI
  input_params: SkillInputParam[];
  secret_ref: string;
  instruction: string;                 // markdown playbook (instruction skills)
}

/**
 * Arya "Setup" drawer — a self-contained side panel with a left sub-nav and
 * six panes (Channels / Integrations / Skills / Memory / Credit Usage /
 * Settings). Opened from the chat header gear button.
 *
 * Self-contained by design: it does NOT touch the chat message flow,
 * typewriter, or history load. It only calls the Setup endpoint groups on
 * AssistantService, all of which are tenant-scoped to the caller's own JWT +
 * entity_id. Secret VALUES are never displayed — only names.
 */
@Component({
  selector: 'codemiro-arya-setup',
  templateUrl: './arya-setup.component.html',
  styleUrls: ['./arya-setup.component.scss'],
})
export class AryaSetupComponent implements OnInit, OnChanges, OnDestroy {
  /** When true, the drawer is rendered + animated in. */
  @Input() open = false;
  /** Emitted when the user closes the drawer (backdrop, ✕, or Escape). */
  @Output() closed = new EventEmitter<void>();
  /** Emitted with a prompt when the user wants to ask Arya something (e.g. create a routine). */
  @Output() askArya = new EventEmitter<string>();

  readonly nav: NavItem[] = [
    { key: 'channels', label: 'Channels' },
    { key: 'tools', label: 'Integrations' },
    { key: 'integrations', label: 'Scheduled Reports' },
    { key: 'skills', label: 'Skills' },
    { key: 'webhooks', label: 'Webhooks' },
    // Memory pane hidden per product decision (2026-06-23).
    { key: 'usage', label: 'Credit Usage' },
    { key: 'settings', label: 'Settings' },
  ];

  readonly integrationsCatalog = [
    { group: 'CRM', items: [{ name: 'Salesforce' }, { name: 'HubSpot' }, { name: 'Zoho CRM' }, { name: 'Pipedrive' }] },
    { group: 'Sales intelligence', items: [{ name: 'Apollo.io' }, { name: 'Lusha' }, { name: 'ZoomInfo' }] },
    { group: 'ERP', items: [{ name: 'SAP' }, { name: 'NetSuite' }, { name: 'Tally' }] },
    { group: 'Ticketing & PM', items: [{ name: 'Jira' }, { name: 'Zendesk' }, { name: 'Freshdesk' }] },
    { group: 'Communication', items: [{ name: 'Slack' }, { name: 'Microsoft Teams' }] },
    { group: 'Automation & Sheets', items: [{ name: 'Zapier' }, { name: 'Make' }, { name: 'Google Sheets' }] },
  ];
  active: Pane = 'skills';
  /** Mobile: show the pane (true) vs the nav list (false). */
  mobileShowPane = false;

  // ── Skills + secrets state ─────────────────────────────────────────────────
  skills: AssistantSkill[] = [];
  skillsLoading = false;
  skillsError = '';
  /** Per-skill inline busy flag (toggle/delete), keyed by skill_id. */
  busySkill: Record<string, boolean> = {};

  // ── Admin capabilities (AGENT_ADMIN only) ─────────────────────────────────
  adminCapabilities: AdminCapability[] = [];
  adminCapsLoading = false;
  adminCapsError = '';
  /** Per-capability busy flag while a toggle PUT is in-flight. */
  busyCap: Record<string, boolean> = {};

  // ── Skill catalog (predefined, browse + search + install) ───────────────────
  catalog: any[] = [];
  catalogLoading = false;
  canAddSkills = false;            // owner-only: gates the "Add skill" / upload UI
  skillSearch = '';
  installing: Record<string, boolean> = {};

  secrets: string[] = [];
  secretsLoading = false;
  secretsError = '';
  newSecretName = '';
  newSecretValue = '';
  secretSaving = false;
  secretFormError = '';

  /** The add/edit form; null when no form is open. */
  form: SkillForm | null = null;
  formSaving = false;
  formError = '';
  instructionDragOver = false;

  // ── Usage state ────────────────────────────────────────────────────────────
  usagePeriod: Period = 'today';
  usageReport: UsageReport | null = null;
  usageLoading = false;
  usageError = '';

  // ── Routines state ─────────────────────────────────────────────────────────
  routines: AssistantRoutine[] = [];
  routinesLoading = false;
  routinesError = '';
  busyRoutine: Record<string, boolean> = {};

  // ── Memory state ────────────────────────────────────────────────────────────
  memoryProfile: any = null;
  memoryLoading = false;
  memoryError = '';

  // ── Feature flags — read from environment.arya ────────────────────────────
  /** Whether the QR-scan connect block is enabled (env flag). */
  get ffQrConnect(): boolean {
    const arya = (environment as any).arya;
    return !!(arya && arya.whatsappQrConnect);
  }
  /** Whether the typed-number + Save field is enabled (env flag). */
  get ffNumberInput(): boolean {
    const arya = (environment as any).arya;
    return !!(arya && arya.whatsappNumberInput);
  }

  // ── Channels state (WhatsApp recipient number) ──────────────────────────────
  channelsLoading = false;
  /** The saved recipient number (full E.164-ish "+91XXXXXXXXXX"), '' = none saved. */
  waRecipient = '';
  /** Country-code part of the editor (default +91). */
  waCountryCode = '+91';
  /** Local (national) digits part of the editor. */
  waNumberInput = '';
  /** True while showing the input form (no saved number, or user clicked Edit). */
  waEditing = false;
  waSaving = false;
  waError = '';                 // inline validation / save error
  /** True when the recipient endpoints are absent on the backend (404). */
  waNotAvailable = false;
  /** True when /whatsapp/status reports the connection is authenticated and can send. */
  waConnected = false;
  /** Number returned by /whatsapp/status when authenticated. */
  waConnectedNumber = '';

  // ── QR connect state ────────────────────────────────────────────────────────
  /** Base64 data-URI for the QR image, '' = none yet. */
  waQrDataUrl = '';
  /** True while creating the session or fetching the QR. */
  waQrLoading = false;
  /** Error string when QR fetch fails; 'unavailable' = endpoint absent. */
  waQrError = '';
  /** True when the go_bridge session is authenticated (phone paired). */
  waQrAuthenticated = false;
  /** Phone number returned after authentication. */
  waQrNumber = '';
  /** Timer handle for the status-poll interval. */
  private _waQrPollTimer: any = null;

  constructor(
    public assistant: AssistantService,
  ) {}

  /** True when the logged-in user carries the AGENT_ADMIN role. */
  get isAgentAdmin(): boolean {
    try {
      const cu = JSON.parse(localStorage.getItem(CURRENT_USER) || 'null');
      const userRoles: string[] = (cu && cu.roles) || [];
      return userRoles.includes('AGENT_ADMIN');
    } catch (_) { return false; }
  }

  ngOnInit(): void {
    if (this.open) { this.onOpened(); }
  }

  /** Re-fetch the active pane whenever the drawer transitions to open. */
  ngOnChanges(): void {
    if (this.open && !this.skills.length && !this.skillsLoading) {
      this.onOpened();
    }
  }

  private onOpened(): void {
    this.select(this.active);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open) { this.close(); }
  }

  close(): void {
    this.closed.emit();
  }

  select(pane: Pane): void {
    this.active = pane;
    this.mobileShowPane = true;
    this.instructionDragOver = false;
    if (pane === 'skills') {
      if (!this.skills.length) { this.loadSkills(); }
      if (!this.catalog.length) { this.loadCatalog(); }
    } else if (pane === 'webhooks') {
      if (!this.skills.length) { this.loadSkills(); }
      if (!this.secrets.length) { this.loadSecrets(); }
      if (!this.catalog.length) { this.loadCatalog(); }
    } else if (pane === 'usage') {
      this.loadUsage();
    } else if (pane === 'integrations') {
      if (!this.routines.length) { this.loadRoutines(); }
    } else if (pane === 'channels') {
      this.loadChannels();
    } else if (pane === 'memory') {
      this.loadMemory();
    } else if (pane === 'settings') {
      if (!this.skills.length) { this.loadSkills(); }
      if (this.isAgentAdmin && !this.adminCapabilities.length) { this.loadAdminCapabilities(); }
    }
  }

  backToNav(): void { this.mobileShowPane = false; }

  private msg(e: any): string {
    if (e instanceof AssistantApiError) { return e.message; }
    return 'Something went wrong. Please try again.';
  }

  // ── Skills ─────────────────────────────────────────────────────────────────

  get webhookSkills(): AssistantSkill[] {
    return this.skills.filter(s => s.type === 'webhook');
  }
  get instructionSkills(): AssistantSkill[] {
    return this.skills.filter(s => s.type === 'instruction');
  }
  get builtinSkills(): AssistantSkill[] {
    return this.skills.filter(s => s.type === 'builtin');
  }

  async loadSkills(): Promise<void> {
    if (this.skillsLoading) { return; }
    this.skillsLoading = true;
    this.skillsError = '';
    try {
      this.skills = await this.assistant.listSkills();
    } catch (e) {
      this.skillsError = this.msg(e);
    } finally {
      this.skillsLoading = false;
    }
  }

  // ── Admin capabilities ─────────────────────────────────────────────────────

  async loadAdminCapabilities(): Promise<void> {
    if (this.adminCapsLoading) { return; }
    this.adminCapsLoading = true;
    this.adminCapsError = '';
    try {
      this.adminCapabilities = await this.assistant.listAdminCapabilities();
    } catch (e) {
      this.adminCapsError = this.msg(e);
    } finally {
      this.adminCapsLoading = false;
    }
  }

  async toggleAdminCapability(cap: AdminCapability): Promise<void> {
    if (this.busyCap[cap.key]) { return; }
    this.busyCap[cap.key] = true;
    const next = !cap.enabled;
    try {
      await this.assistant.setAdminCapability(cap.key, next);
      cap.enabled = next;
      this.adminCapsError = '';
    } catch (e) {
      this.adminCapsError = this.msg(e);
    } finally {
      this.busyCap[cap.key] = false;
    }
  }

  // ── Skill catalog ───────────────────────────────────────────────────────────
  async loadCatalog(): Promise<void> {
    this.catalogLoading = true;
    try {
      const res = await this.assistant.getSkillCatalog();
      this.catalog = (res && res.catalog) || [];
      this.canAddSkills = !!(res && res.can_add);
    } catch (_) {
      this.catalog = [];
    } finally {
      this.catalogLoading = false;
    }
  }

  /** Full catalog filtered by the search box (name / description / keywords), minus already-installed. */
  get filteredCatalog(): any[] {
    const q = (this.skillSearch || '').trim().toLowerCase();
    const installedKeys = new Set(this.skills.map(s => (s.name || '').toLowerCase()));
    const list = this.catalog.filter(t => !installedKeys.has((t.name || '').toLowerCase()));
    if (!q) { return list; }
    return list.filter(t =>
      (t.name || '').toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q) ||
      (t.trigger_keywords || []).join(' ').toLowerCase().includes(q));
  }

  /** Catalog items for the Skills tab — instruction-type templates only. */
  get filteredInstructionCatalog(): any[] {
    return this.filteredCatalog.filter(t => t.type === 'instruction');
  }

  /** Catalog items for the Webhooks tab — non-instruction templates (External API). */
  get filteredWebhookCatalog(): any[] {
    return this.filteredCatalog.filter(t => t.type !== 'instruction');
  }

  /** Install a catalog skill. Ready ones create directly; API ones open the
   *  form prefilled so the user supplies their endpoint. */
  async installSkill(t: any): Promise<void> {
    if (!this.canAddSkills) {
      this.skillsError = 'Only an admin can add skills.';
      return;
    }
    if (!t.ready) {
      // Prefill the add form (e.g. CRM / Slack need the user's URL).
      this.newSkill();
      if (this.form) {
        this.form.kind = t.type === 'instruction' ? 'instruction' : 'webhook';
        this.form.name = t.name || '';
        this.form.description = t.description || '';
        this.form.method = t.method || 'POST';
        this.form.url = t.url || '';
        this.form.trigger_keywords = (t.trigger_keywords || []).join(', ');
        this.form.input_params = (t.input_params || []).map((p: any) => ({ ...p }));
        this.form.instruction = t.instruction || '';
      }
      return;
    }
    if (this.installing[t.key]) { return; }
    this.installing[t.key] = true;
    this.skillsError = '';
    try {
      const payload: any = t.type === 'instruction'
        ? { type: 'instruction', name: t.name, description: t.description,
            trigger_keywords: t.trigger_keywords || [], instruction: t.instruction }
        : { type: 'external_api', name: t.name, description: t.description, method: t.method,
            url: t.url, trigger_keywords: t.trigger_keywords || [], input_params: t.input_params || [] };
      const created = await this.assistant.createSkill(payload);
      if (created && created.skill_id) { this.skills = [created, ...this.skills]; }
      else { await this.loadSkills(); }
    } catch (e) {
      this.skillsError = this.msg(e);
    } finally {
      this.installing[t.key] = false;
    }
  }

  async toggleSkill(skill: AssistantSkill): Promise<void> {
    if (this.busySkill[skill.skill_id]) { return; }
    this.busySkill[skill.skill_id] = true;
    const next = !skill.enabled;
    try {
      await this.assistant.updateSkill(skill.skill_id, { enabled: next });
      skill.enabled = next;
      this.skillsError = '';
    } catch (e) {
      this.skillsError = this.msg(e);
    } finally {
      this.busySkill[skill.skill_id] = false;
    }
  }

  async deleteSkill(skill: AssistantSkill): Promise<void> {
    if (this.busySkill[skill.skill_id]) { return; }
    if (!confirm(`Delete skill "${skill.name || skill.skill_id}"? This cannot be undone.`)) { return; }
    this.busySkill[skill.skill_id] = true;
    try {
      await this.assistant.deleteSkill(skill.skill_id);
      this.skills = this.skills.filter(s => s.skill_id !== skill.skill_id);
      this.skillsError = '';
    } catch (e) {
      this.skillsError = this.msg(e);
    } finally {
      this.busySkill[skill.skill_id] = false;
    }
  }

  // ── Add / edit form ──────────────────────────────────────────────────────

  /** Opens the add form pre-set to instruction playbook (for the Skills tab). */
  newSkill(): void {
    this.formError = '';
    this.form = {
      kind: 'instruction',
      name: '',
      description: '',
      method: 'POST',
      url: '',
      trigger_keywords: '',
      input_params: [],
      secret_ref: '',
      instruction: '',
    };
  }

  /** Opens the add form pre-set to webhook / External API (for the Webhooks tab). */
  newWebhook(): void {
    this.formError = '';
    this.form = {
      kind: 'webhook',
      name: '',
      description: '',
      method: 'POST',
      url: '',
      trigger_keywords: '',
      input_params: [],
      secret_ref: '',
      instruction: '',
    };
  }

  editSkill(skill: AssistantSkill): void {
    this.formError = '';
    this.form = {
      skill_id: skill.skill_id,
      kind: skill.type === 'instruction' ? 'instruction' : 'webhook',
      name: skill.name || '',
      description: skill.description || '',
      method: skill.method || 'POST',
      url: skill.url || '',
      trigger_keywords: (skill.trigger_keywords || []).join(', '),
      input_params: (skill.input_params || []).map(p => ({ ...p })),
      secret_ref: skill.secret_ref || '',
      instruction: (skill as any).instruction || '',
    };
  }

  /** Read a dropped/selected .md (or text) file into the instruction field. */
  onInstructionFile(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input && input.files && input.files[0];
    if (file) { this.readInstructionFile(file); }
    if (input) { input.value = ''; }
  }

  onInstructionDrop(ev: DragEvent): void {
    ev.preventDefault();
    this.instructionDragOver = false;
    const file = ev.dataTransfer && ev.dataTransfer.files && ev.dataTransfer.files[0];
    if (file) { this.readInstructionFile(file); }
  }

  private readInstructionFile(file: File): void {
    if (!this.form) { return; }
    const name = (file.name || '').toLowerCase();
    if (!/\.(md|markdown|txt)$/.test(name)) {
      this.formError = 'Please upload a .md, .markdown or .txt file.';
      return;
    }
    if (file.size > 200 * 1024) { this.formError = 'File is too large (max 200 KB).'; return; }
    const reader = new FileReader();
    reader.onload = () => {
      if (!this.form) { return; }
      this.form.instruction = String(reader.result || '');
      if (!this.form.name.trim()) {
        this.form.name = (file.name || '').replace(/\.(md|markdown|txt)$/i, '');
      }
      this.formError = '';
    };
    reader.readAsText(file);
  }

  cancelForm(): void {
    this.form = null;
    this.formError = '';
    this.instructionDragOver = false;
  }

  addParamRow(): void {
    if (!this.form) { return; }
    this.form.input_params.push({ name: '', type: 'string', description: '' });
  }

  removeParamRow(i: number): void {
    if (!this.form) { return; }
    this.form.input_params.splice(i, 1);
  }

  private validateForm(f: SkillForm): string {
    if (!f.name.trim()) { return 'Please give the skill a name.'; }
    if (f.kind === 'instruction') {
      if (f.instruction.trim().length < 20) {
        return 'Add the instruction/playbook (upload a .md file or paste it — at least a sentence or two).';
      }
      return '';
    }
    if (!f.url.trim()) { return 'Please enter the External API URL.'; }
    if (!/^https:\/\//i.test(f.url.trim())) { return 'The URL must start with https://'; }
    for (const p of f.input_params) {
      if (!p.name.trim()) { return 'Every input parameter needs a name (or remove the empty row).'; }
    }
    return '';
  }

  async saveSkill(): Promise<void> {
    if (!this.form || this.formSaving) { return; }
    const f = this.form;
    const v = this.validateForm(f);
    if (v) { this.formError = v; return; }

    this.formSaving = true;
    this.formError = '';
    const keywords = f.trigger_keywords.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
    let payload: Partial<AssistantSkill>;
    if (f.kind === 'instruction') {
      payload = {
        type: 'instruction',
        name: f.name.trim(),
        description: f.description.trim(),
        trigger_keywords: keywords,
        instruction: f.instruction,
      } as any;
    } else {
      payload = {
        type: 'external_api' as any,   // backend aliases to webhook
        name: f.name.trim(),
        description: f.description.trim(),
        method: f.method,
        url: f.url.trim(),
        trigger_keywords: keywords,
        input_params: f.input_params
          .filter(p => p.name.trim())
          .map(p => ({ name: p.name.trim(), type: p.type || 'string', description: (p.description || '').trim() })),
      };
      if (f.secret_ref.trim()) { payload.secret_ref = f.secret_ref.trim(); }
    }

    try {
      if (f.skill_id) {
        const updated = await this.assistant.updateSkill(f.skill_id, payload);
        const idx = this.skills.findIndex(s => s.skill_id === f.skill_id);
        if (idx >= 0) {
          this.skills[idx] = updated && updated.skill_id ? updated : { ...this.skills[idx], ...payload, skill_id: f.skill_id } as AssistantSkill;
        }
      } else {
        const created = await this.assistant.createSkill(payload);
        if (created && created.skill_id) {
          this.skills = [created, ...this.skills];
        } else {
          await this.loadSkills();
        }
      }
      this.form = null;
    } catch (e) {
      // Surfaces backend validation inline (e.g. SSRF "url host resolves to a blocked address").
      this.formError = this.msg(e);
    } finally {
      this.formSaving = false;
    }
  }

  // ── Secrets ──────────────────────────────────────────────────────────────

  async loadSecrets(): Promise<void> {
    this.secretsLoading = true;
    this.secretsError = '';
    try {
      this.secrets = await this.assistant.listSecrets();
    } catch (e) {
      this.secretsError = this.msg(e);
    } finally {
      this.secretsLoading = false;
    }
  }

  async addSecret(): Promise<void> {
    if (this.secretSaving) { return; }
    const name = this.newSecretName.trim();
    const value = this.newSecretValue;
    this.secretFormError = '';
    if (!name) { this.secretFormError = 'Please enter a name for the secret.'; return; }
    if (!value) { this.secretFormError = 'Please enter the secret value.'; return; }

    this.secretSaving = true;
    try {
      await this.assistant.createSecret(name, value);
      if (!this.secrets.includes(name)) { this.secrets = [...this.secrets, name]; }
      this.newSecretName = '';
      this.newSecretValue = '';   // never retain the value in memory longer than needed
    } catch (e) {
      this.secretFormError = this.msg(e);
    } finally {
      this.secretSaving = false;
    }
  }

  async deleteSecret(name: string): Promise<void> {
    if (!confirm(`Delete secret "${name}"? Skills referencing it will stop working.`)) { return; }
    this.secretsError = '';
    try {
      await this.assistant.deleteSecret(name);
      this.secrets = this.secrets.filter(s => s !== name);
    } catch (e) {
      this.secretsError = this.msg(e);
    }
  }

  // ── Usage ──────────────────────────────────────────────────────────────────

  setPeriod(p: Period): void {
    this.usagePeriod = p;
  }

  get currentUsage(): UsagePeriod | null {
    if (!this.usageReport) { return null; }
    return this.usageReport[this.usagePeriod] || null;
  }

  async loadUsage(): Promise<void> {
    this.usageLoading = true;
    this.usageError = '';
    try {
      this.usageReport = await this.assistant.usage(this.usagePeriod);
    } catch (e) {
      this.usageError = this.msg(e);
    } finally {
      this.usageLoading = false;
    }
  }

  // ── Routines ─────────────────────────────────────────────────────────────

  async loadRoutines(): Promise<void> {
    this.routinesLoading = true;
    this.routinesError = '';
    try {
      this.routines = await this.assistant.listRoutines();
    } catch (e) {
      this.routinesError = this.msg(e);
    } finally {
      this.routinesLoading = false;
    }
  }

  // ── Channels (WhatsApp recipient number) ────────────────────────────────────

  /** Load channels state: real connection status + recipient number (if flag on). */
  async loadChannels(): Promise<void> {
    this.channelsLoading = true;
    this.waError = '';
    this.waNotAvailable = false;
    try {
      // Parallel: load connection status, QR status (if flag on), recipient number (if flag on).
      const [, recipientRes] = await Promise.all([
        // Always fetch real /status so the banner reflects truth (fail-safe on 404).
        this.loadWaConnectionStatus().then(() =>
          this.ffQrConnect ? this.loadQrStatus() : Promise.resolve()
        ),
        this.ffNumberInput ? this.assistant.getWhatsappRecipient().catch((e: any) => {
          if (e instanceof AssistantApiError && e.status === 404) {
            this.waNotAvailable = true;
          } else {
            this.waError = this.msg(e);
          }
          return { phone: '' };
        }) : Promise.resolve({ phone: '' }),
      ]);
      if (this.ffNumberInput) {
        const phone = (recipientRes && (recipientRes as { phone?: string }).phone) || '';
        this.setRecipientFromPhone(phone);
        this.waEditing = !this.waRecipient;
      }
    } finally {
      this.channelsLoading = false;
    }
  }

  /** Fetch real send-path status from /whatsapp/status; treat 404 as not-connected (fail-safe). */
  private async loadWaConnectionStatus(): Promise<void> {
    try {
      const st = await this.assistant.getWhatsappStatus();
      this.waConnected = !!(st && st.authenticated);
      this.waConnectedNumber = (st && st.number) || '';
    } catch (e) {
      // 404 = endpoint not live yet; any error → treat as not connected.
      this.waConnected = false;
      this.waConnectedNumber = '';
    }
  }

  /** Fetch current QR status from the backend; no-throw (degrades gracefully).
   *  Note: loadWaConnectionStatus() already called getWhatsappStatus() in loadChannels.
   *  This method syncs the QR-scan-flow local fields from the already-populated waConnected state. */
  private async loadQrStatus(): Promise<void> {
    // Reuse the status already fetched by loadWaConnectionStatus (called before this in loadChannels).
    this.waQrAuthenticated = this.waConnected;
    this.waQrNumber = this.waConnectedNumber;
    if (this.waQrAuthenticated) { this._stopQrPoll(); }
  }

  /** Start a QR session (or refresh an expired one): POST connect → GET qr → poll status. */
  async startQrSession(): Promise<void> {
    if (this.waQrLoading) { return; }
    this.waQrLoading = true;
    this.waQrError = '';
    this.waQrDataUrl = '';
    this.waQrAuthenticated = false;
    this.waQrNumber = '';
    this._stopQrPoll();
    try {
      const session = await this.assistant.createWhatsappSession();
      // Backend may return the QR inline on connect.
      const inlineQr = session && session.qr_data_url;
      if (inlineQr) {
        this.waQrDataUrl = inlineQr;
      } else {
        // Fetch QR separately.
        try {
          const qrRes = await this.assistant.getWhatsappQr();
          this.waQrDataUrl = (qrRes && qrRes.qr_data_url) || '';
        } catch (qrErr) {
          if (qrErr instanceof AssistantApiError && qrErr.status === 404) {
            this.waQrError = 'unavailable';
          } else {
            this.waQrError = 'Could not load QR code. Try again.';
          }
          return;
        }
      }
      if (!this.waQrDataUrl) {
        this.waQrError = 'No QR returned by the server. Try again in a moment.';
        return;
      }
      // Begin polling for authentication.
      this._startQrPoll();
    } catch (e) {
      if (e instanceof AssistantApiError && e.status === 404) {
        this.waQrError = 'unavailable';
      } else {
        this.waQrError = this.msg(e);
      }
    } finally {
      this.waQrLoading = false;
    }
  }

  private _startQrPoll(): void {
    this._stopQrPoll();
    // Poll every 4 seconds until authenticated (max ~5 min / 75 ticks).
    let ticks = 0;
    this._waQrPollTimer = setInterval(async () => {
      ticks++;
      if (ticks > 75) { this._stopQrPoll(); return; }
      try {
        const st = await this.assistant.getWhatsappStatus();
        if (st && st.authenticated) {
          this.waQrAuthenticated = true;
          this.waQrNumber = st.number || '';
          this.waConnected = true;
          this.waConnectedNumber = st.number || '';
          this.waQrDataUrl = '';   // QR no longer needed — hide it.
          this._stopQrPoll();
        }
      } catch (_) { /* network blip — keep polling */ }
    }, 4000);
  }

  private _stopQrPoll(): void {
    if (this._waQrPollTimer !== null) {
      clearInterval(this._waQrPollTimer);
      this._waQrPollTimer = null;
    }
  }

  /** Split a stored "+91XXXXXXXXXX" into country-code + national parts for the editor. */
  private setRecipientFromPhone(phone: string): void {
    this.waRecipient = phone || '';
    if (!phone) { return; }
    const m = phone.match(/^(\+\d{1,3})(\d+)$/);
    if (m) {
      this.waCountryCode = m[1];
      this.waNumberInput = m[2];
    } else {
      // Unexpected shape — keep digits in the national field, leave code default.
      this.waNumberInput = phone.replace(/[^\d]/g, '');
    }
  }

  /** Full E.164-ish number from the editor fields. */
  private composePhone(): string {
    const code = (this.waCountryCode || '').replace(/[^\d+]/g, '');
    const num = (this.waNumberInput || '').replace(/[^\d]/g, '');
    const normCode = code.startsWith('+') ? code : `+${code}`;
    return `${normCode}${num}`;
  }

  /** Basic phone validation: + country code then 10–15 total digits. */
  private validPhone(phone: string): boolean {
    if (!/^\+\d+$/.test(phone)) { return false; }
    const digits = phone.replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 15;
  }

  startEditRecipient(): void {
    this.waEditing = true;
    this.waError = '';
  }

  cancelEditRecipient(): void {
    // Restore editor fields from the saved number; keep editing only if none saved.
    this.setRecipientFromPhone(this.waRecipient);
    this.waEditing = !this.waRecipient;
    this.waError = '';
  }

  async saveRecipient(): Promise<void> {
    if (this.waSaving) { return; }
    const phone = this.composePhone();
    if (!this.validPhone(phone)) {
      this.waError = 'Enter a valid number with country code (10–15 digits).';
      return;
    }
    this.waSaving = true;
    this.waError = '';
    try {
      await this.assistant.setWhatsappRecipient(phone);
      this.waRecipient = phone;
      this.waEditing = false;
      this.waNotAvailable = false;
      // Refresh connection status so the banner reflects truth after save.
      await this.loadWaConnectionStatus();
    } catch (e) {
      this.waError = this.msg(e);
    } finally {
      this.waSaving = false;
    }
  }

  async removeRecipient(): Promise<void> {
    if (!this.waRecipient) { return; }
    if (!confirm('Remove this WhatsApp number? Scheduled reports will stop being sent.')) { return; }
    this.waError = '';
    try {
      await this.assistant.deleteWhatsappRecipient();
    } catch (e) {
      // Any error (including 404 = backend not live): keep the number, show error, do NOT clear UI.
      this.waError = this.msg(e);
      return;
    }
    // DELETE succeeded — re-fetch to confirm truth, then clear UI.
    try {
      const recipientRes = await this.assistant.getWhatsappRecipient();
      const phone = (recipientRes && recipientRes.phone) || '';
      this.setRecipientFromPhone(phone);
      this.waEditing = !this.waRecipient;
    } catch (_) {
      // Re-fetch failed — clear locally since DELETE was confirmed successful.
      this.waRecipient = '';
      this.waNumberInput = '';
      this.waCountryCode = '+91';
      this.waEditing = true;
    }
    // Refresh connection status after removal.
    await this.loadWaConnectionStatus();
  }

  // ── Memory ───────────────────────────────────────────────────────────────────

  async loadMemory(): Promise<void> {
    if (this.memoryLoading) { return; }
    this.memoryLoading = true;
    this.memoryError = '';
    try {
      this.memoryProfile = await this.assistant.getMemory();
    } catch (e) {
      // 404 means the backend feature flag is off — treat as empty, not hard error
      if (e instanceof AssistantApiError && e.status === 404) {
        this.memoryProfile = null;
      } else {
        this.memoryError = this.msg(e);
      }
    } finally {
      this.memoryLoading = false;
    }
  }

  async clearMemory(): Promise<void> {
    this.memoryError = '';
    try {
      await this.assistant.clearMemory();
      this.memoryProfile = null;
    } catch (e) {
      this.memoryError = this.msg(e);
    }
  }

  ngOnDestroy(): void {
    this._stopQrPoll();
  }

  // ── Downloads / export ──────────────────────────────────────────────────────
  downloading = false;
  downloadError = '';
  downloadReports = [
    { key: 'account_summary', label: 'Account summary' },
    { key: 'campaigns', label: 'Credit utilization by campaign' },
    { key: 'leads', label: 'Leads' },
  ];

  async download(dataType: string, fmt: 'csv' | 'xlsx' | 'pdf'): Promise<void> {
    if (this.downloading) { return; }
    this.downloading = true;
    this.downloadError = '';
    try {
      const res = await this.assistant.exportReport(dataType, fmt);
      // base64 → Blob → trigger browser download.
      const bin = atob(res.base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) { bytes[i] = bin.charCodeAt(i); }
      const blob = new Blob([bytes], { type: res.mime || 'application/octet-stream' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = res.filename || `arya_${dataType}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 0);
    } catch (e) {
      this.downloadError = this.msg(e);
    } finally {
      this.downloading = false;
    }
  }

  async deleteRoutine(r: AssistantRoutine): Promise<void> {
    if (this.busyRoutine[r.routine_id]) { return; }
    if (!confirm(`Delete routine "${r.name || r.routine_id}"?`)) { return; }
    this.busyRoutine[r.routine_id] = true;
    try {
      await this.assistant.deleteRoutine(r.routine_id);
      this.routines = this.routines.filter(x => x.routine_id !== r.routine_id);
      this.routinesError = '';
    } catch (e) {
      this.routinesError = this.msg(e);
    } finally {
      this.busyRoutine[r.routine_id] = false;
    }
  }

  routineWhen(r: AssistantRoutine): string {
    const s = r.schedule || {};
    const type = s.type || '';
    const time = s.time_ist ? ` at ${s.time_ist} IST` : '';
    if (!type && !time) { return 'Schedule set'; }
    return `${type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Scheduled'}${time}`;
  }

  routineAction(r: AssistantRoutine): string {
    return (r.action && r.action.type) ? r.action.type : '—';
  }

  /** Pre-fill the chat input with a sample routine prompt for the user to review and send manually. */
  insertSampleRoutinePrompt(): void {
    // Emit the sample text so the parent fills the chat draft box — the user
    // reviews and sends manually (not auto-sent).
    this.askArya.emit('__PREFILL__Every day at 8pm, send me a WhatsApp summary of my account\'s lead progress and goal status.');
    this.close();
  }

  /** @deprecated Kept for backwards compat; use insertSampleRoutinePrompt. */
  promptRoutine(): void {
    this.insertSampleRoutinePrompt();
  }

  /** Number formatting helpers for the usage pane. */
  fmtTokens(n: number | undefined): string {
    if (n === undefined || n === null) { return '0'; }
    if (n >= 1_000_000) { return (n / 1_000_000).toFixed(2) + 'M'; }
    if (n >= 1_000) { return (n / 1_000).toFixed(1) + 'K'; }
    return n.toLocaleString('en-IN');
  }
  /** Plain integer (e.g. AI-request count) — never abbreviated to M/K. */
  fmtInt(n: number | undefined): string {
    if (n === undefined || n === null) { return '0'; }
    return n.toLocaleString('en-IN');
  }
  fmtUsd(n: number | undefined): string {
    if (n === undefined || n === null) { return '$0.00'; }
    return '$' + n.toFixed(4);
  }
  fmtInr(n: number | undefined): string {
    if (n === undefined || n === null) { return '₹0.00'; }
    return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}
